import { useAppSelector } from '@hooks/redux.hooks';
import { useCameraCommand } from '@hooks/useCameraCommand';
import { selectJoystickControlledCam } from '@redux/global/global.selector';
import { selectCameraMonitoringSensorByUniqueKey } from '@redux/situation/monitoring.selectors';
import { debounce, isEqual, throttle } from 'lodash';
import { useEffect, useState } from 'react';
import GamePad from 'react-gamepad';

import { FocusModeEnum } from '@/types/sensor/sensors';

type PadAxisState = {
  [key in AxisNameEnum]?: number;
};

enum AxisNameEnum {
  AXIS_0 = 'Axis0',
  AXIS_1 = 'Axis1',
  AXIS_9 = 'Axis9',
  LEFT_STICK_X = 'LeftStickX',
  LEFT_STICK_Y = 'LeftStickY',
  RIGHT_STICK_X = 'RightStickX',
}

const isAxisNameEnum = (value: string): value is AxisNameEnum => {
  return Object.values(AxisNameEnum).includes(value as AxisNameEnum);
};

export default function JoystickControl() {
  const [padAxis, setPadAxis] = useState<PadAxisState>({});
  const [isZooming, setIsZooming] = useState<'in' | 'out' | null>(null);

  const controlledCamera = useAppSelector(selectJoystickControlledCam);
  const monitoringSensor = useAppSelector(
    (state) => selectCameraMonitoringSensorByUniqueKey(state, false, controlledCamera.cameraUniqueKey),
    isEqual,
  );

  const subCameraStatus =
    monitoringSensor?.status?.videoStatuses.find((videoStatus) => videoStatus.id === controlledCamera.subCameraId) ??
    null;
  const isAutoFocusOn = subCameraStatus?.focusMode === FocusModeEnum.AUTO;

  const useCamera = useCameraCommand({
    monitoringSensor,
    openedSubCameraId: controlledCamera.subCameraId,
  });

  const axisZoomName = [AxisNameEnum.RIGHT_STICK_X];
  const axisMoveName = [
    AxisNameEnum.AXIS_0,
    AxisNameEnum.AXIS_1,
    AxisNameEnum.AXIS_9,
    AxisNameEnum.LEFT_STICK_X,
    AxisNameEnum.LEFT_STICK_Y,
  ];

  const onMoveEnd = debounce(() => {
    useCamera.move({ panSpeed: 0, tiltSpeed: 0 });
  }, 100);

  const onAxisChange = (axis: string, value: number, newPadAxis: PadAxisState) => {
    if (!useCamera.canCommand) {
      return;
    }
    // Zoom
    if (isAxisNameEnum(axis) && axisZoomName.includes(axis)) {
      if (value > 0 && isZooming !== 'in') {
        setIsZooming('in');
        useCamera.zoomIn();
      } else if (value < 0 && isZooming !== 'out') {
        setIsZooming('out');
        useCamera.zoomOut();
      } else if (value === 0) {
        setIsZooming(null);
        useCamera.zoomStop();
      }
    }

    // azimuth & site
    if (isAxisNameEnum(axis) && axisMoveName.includes(axis)) {
      if (newPadAxis[axis]) {
        const dx = newPadAxis[AxisNameEnum.LEFT_STICK_X] ?? newPadAxis[AxisNameEnum.AXIS_0] ?? 0;
        const dy = newPadAxis[AxisNameEnum.LEFT_STICK_Y] ?? newPadAxis[AxisNameEnum.AXIS_1] ?? 0;
        useCamera.moveJoystick(dx, dy);
      } else {
        onMoveEnd();
      }
    }
  };

  const updateAxis = throttle((axisName: string | null, originalValue: number | null | undefined) => {
    if (axisName && originalValue !== undefined && originalValue !== null && !isNaN(originalValue)) {
      const invert = axisName.startsWith('-');
      const value = originalValue * (invert ? -1 : 1);

      if (invert) {
        axisName = axisName.substring(1);
      }

      if (isAxisNameEnum(axisName) && (padAxis[axisName] === undefined || +padAxis[axisName]! !== value)) {
        const newPadAxis: PadAxisState = { ...padAxis, [axisName]: value };
        setPadAxis(newPadAxis);
        onAxisChange(axisName, value, newPadAxis);
      }
    }
  }, 100);

  const axisIndexToAxisName = (index: number) => {
    return `Axis${index}`;
  };

  const updateAllAxisValue = (gamepad: Gamepad) => {
    for (let i = 0; i < gamepad.axes.length; ++i) {
      const axisName = axisIndexToAxisName(i);

      updateAxis(axisName, gamepad.axes[i]);
    }
  };

  const handleButton = (buttonName: string, pressed: boolean) => {
    switch (buttonName) {
      case 'LT':
      case 'DPadLeft':
        pressed ? useCamera.focusIn() : useCamera.focusStop();
        break;
      case 'RT':
      case 'DPadRight':
        pressed ? useCamera.focusOut() : useCamera.focusStop();
        break;
      case 'RB':
      case 'DPadDown':
        if (pressed) {
          isAutoFocusOn ? useCamera.focusStop() : useCamera.focusAuto();
        }
        break;
      case 'LB':
      case 'DPadUp':
        if (pressed) {
          useCamera.videoBuffering();
        }
        break;
      default:
        console.log(`${buttonName} is ${pressed ? 'pressed' : 'not pressed'}.`);
    }
  };

  const updateGamePad = (gamepadIndex: number) => {
    if (window.isSecureContext) {
      const gamepad = navigator.getGamepads().find((gp) => gp?.index === gamepadIndex);
      gamepad && updateAllAxisValue(gamepad);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    let move = null;

    if (!useCamera.canCommand || event.repeat || !monitoringSensor?.configuration) {
      return;
    }

    const speedFactor = 0.1;
    const maxPanSpeed = monitoringSensor.configuration.vxMax;
    const maxTiltSpeed = monitoringSensor.configuration.vyMax;

    switch (event.key) {
      case 'ArrowUp':
        move = { panSpeed: 0, tiltSpeed: speedFactor * maxTiltSpeed };
        break;
      case 'ArrowDown':
        move = { panSpeed: 0, tiltSpeed: -speedFactor * maxTiltSpeed };
        break;
      case 'ArrowLeft':
        move = { panSpeed: -speedFactor * maxPanSpeed, tiltSpeed: 0 };
        break;
      case 'ArrowRight':
        move = { panSpeed: speedFactor * maxPanSpeed, tiltSpeed: 0 };
        break;
      case '+':
        useCamera.zoomIn();
        break;
      case '-':
        useCamera.zoomOut();
        break;
      default:
        break;
    }
    move && useCamera.move(move);
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    if (!useCamera.canCommand) {
      return;
    }
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'ArrowRight':
        useCamera.move({ panSpeed: 0, tiltSpeed: 0 });
        break;
      case '+':
      case '-':
        useCamera.zoomStop();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    updateGamePad(0);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      // force stop move on joystickControl removed
      if (!useCamera.canCommand && monitoringSensor && !monitoringSensor.status?.roundOn) {
        useCamera.move({ panSpeed: 0, tiltSpeed: 0 });
        useCamera.zoomStop();
      }
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useCamera]);

  return (
    <GamePad
      onConnect={updateGamePad}
      onButtonChange={handleButton}
      onDisconnect={updateGamePad}
      onAxisChange={updateAxis}
    >
      <div className="gamepad"></div>
    </GamePad>
  );
}
